/**
 * quick-add-stock.js
 * ==================
 * Dispatcht Backorder-Events nach Cart-Änderungen auf allen Seiten.
 *
 * Inventory-Quelle für Toast-Entscheidung:
 *   PDP:             #product-inventory-data  (enc-Format, Base64)
 *   Collection/Search: #plp-inventory-data    (enc-Format, Base64)
 *   Beide werden beim Init in plpInventoryMap geladen.
 *
 * Zwei Trigger-Pfade:
 *
 * A) Dawn PubSub cartUpdate – primärer Pfad (alle Seiten):
 *    Wird von quick-add-bulk.js, quick-order-list.js und product-form.js
 *    nach jedem erfolgreichen cart/update.js oder cart/add.js gefeuert.
 *    cartData.items enthält den vollständigen Cart-State.
 *
 * B) Cart-Drawer-MutationObserver – Fallback für Seiten MIT Cart-Drawer:
 *    Beobachtet #CartDrawer-CartItems auf DOM-Swaps, fetcht /cart.js
 *    und dispatcht backorder:cart-updated.
 */

const BACKORDER_EVENT       = 'backorder:cart-updated';
const QUICK_ADD_TOAST_EVENT = 'backorder:quick-add-toast';
const CART_API              = '/cart.js';
const DEBOUNCE_MS           = 80;

const observedTargets = new WeakSet();

/**
 * Speichert den letzten bekannten Cart-State: variantId → quantity.
 * Wird genutzt um nur Items zu toasten deren Menge sich erhöht hat —
 * verhindert False-Positive-Toasts für bereits im Cart liegende Backorder-Artikel.
 * @type {Map<number, number>}
 */
let previousCartQuantities = new Map();

/**
 * Lokale Inventory-Map: variantId (number) → inventory_quantity (number)
 * Enthält nur Varianten mit inventory_management=shopify + inventory_policy=continue.
 * Gebaut aus #plp-inventory-data oder #product-inventory-data (enc-Format).
 *
 * @type {Map<number, number>}
 */
let plpInventoryMap = new Map();

/**
 * Dekodiert ein enc-Feld: atob("shopify|continue|5") → { management, policy, qty }
 * Gibt null zurück bei Fehler.
 */
function decodeEnc(enc) {
  try {
    const [management, policy, qtyStr] = atob(enc).split('|');
    const qty = parseInt(qtyStr, 10);
    if (isNaN(qty)) return null;
    return { management, policy, qty };
  } catch {
    return null;
  }
}

/**
 * Baut plpInventoryMap aus einem JSON-Script-Element.
 * Erwartet Format: { "variants": [{ "id": 123, "enc": "..." }, ...] }
 */
function loadInventoryFromScript(scriptEl) {
  if (!scriptEl) return;
  let data;
  try { data = JSON.parse(scriptEl.textContent); } catch { return; }
  for (const v of data?.variants ?? []) {
    if (!v.enc) continue;
    const decoded = decodeEnc(v.enc);
    if (!decoded) continue;
    if (decoded.management === 'shopify' && decoded.policy === 'continue') {
      plpInventoryMap.set(Number(v.id), decoded.qty);
    }
  }
}

/** Initialisiert plpInventoryMap aus #plp-inventory-data und #product-inventory-data. */
function buildPlpInventoryMap() {
  plpInventoryMap = new Map();
  loadInventoryFromScript(document.getElementById('plp-inventory-data'));
  loadInventoryFromScript(document.getElementById('product-inventory-data'));
}

/** Holt das items-Array aus /cart.js; gibt [] zurück bei Fehler. */
async function fetchCartItems() {
  try {
    const res = await fetch(CART_API, {
      method:  'GET',
      headers: { 'Content-Type': 'application/json' },
    });
    if (!res.ok) return [];
    const data = await res.json();
    return data?.items ?? [];
  } catch {
    return [];
  }
}

/** Dispatcht backorder:cart-updated auf document. */
function dispatchBackorderEvent(cartItems) {
  document.dispatchEvent(
    new CustomEvent(BACKORDER_EVENT, {
      bubbles: true,
      detail: { cartItems },
    })
  );
}

/**
 * Prüft ob nach einer Cart-Änderung ein Backorder-Toast notwendig ist
 * und dispatcht backorder:quick-add-toast wenn ja.
 *
 * Nur aktiv wenn keine [data-backorder-notice][data-toast-only]-Elemente
 * im DOM sind — auf der PDP übernimmt backorder-quantity-watch.js den Toast.
 *
 * Inventory-Quelle: plpInventoryMap (aus #plp-inventory-data / #product-inventory-data).
 * Ohne bekannten invQty für eine Variante → kein Toast (verhindert False-Positives).
 *
 * False-Positive-Schutz: Nur Items triggern deren Menge sich gegenüber dem
 * vorherigen Cart-State erhöht hat. Bereits im Cart liegende Backorder-Artikel
 * sollen keinen Toast beim nächsten unabhängigen Cart-Update auslösen.
 */
function maybeDispatchQuickAddToast(cartItems) {
  // Auf der PDP übernimmt backorder-quantity-watch.js den Toast via backorder:show-toast.
  // #stock-status ist PDP-exklusiv → zuverlässigerer Guard als [data-toast-only].
  if (document.getElementById('stock-status')) return;

  for (const item of cartItems) {
    const variantId = item.variant_id;
    const qty       = item.quantity ?? 0;

    // Shopify's /cart.js und /cart/update.js liefern kein inventory_policy-Feld.
    // Stattdessen: plpInventoryMap als einzige Quelle nutzen.
    // Nur Varianten die in der Map sind (= inventory_policy=continue) werden geprüft.
    if (!plpInventoryMap.has(variantId)) continue;
    const invQty = plpInventoryMap.get(variantId);

    const isFullBackorder    = invQty <= 0 && qty >= 1;
    const isPartialBackorder = invQty > 0  && qty > invQty;

    if (!isFullBackorder && !isPartialBackorder) continue;

    // False-Positive-Schutz: Menge muss sich erhöht haben.
    // Wenn die Menge unverändert oder gesunken ist, war der Artikel bereits
    // auf Backorder — kein neuer Toast nötig.
    const prevQty = previousCartQuantities.get(variantId) ?? 0;
    if (qty <= prevQty) continue;

    const boQty = isPartialBackorder ? qty - invQty : qty;
    if (boQty <= 0) continue;

    document.dispatchEvent(
      new CustomEvent(QUICK_ADD_TOAST_EVENT, {
        bubbles: false,
        detail: { boQty, variantId },
      })
    );
    break; // Singleton: nur erster Backorder-Artikel
  }

  // previousCartQuantities nach dem Toast-Check aktualisieren
  previousCartQuantities = new Map(cartItems.map(i => [i.variant_id, i.quantity ?? 0]));
}

/** Registriert MutationObserver auf einem Cart-Drawer-Target. Idempotent. */
function attachObserverToTarget(target) {
  if (observedTargets.has(target)) return;

  let debounceTimer = null;
  let isFetching    = false;

  const observer = new MutationObserver((mutations) => {
    if (!mutations.some((m) => m.type === 'childList')) return;
    clearTimeout(debounceTimer);
    debounceTimer = setTimeout(() => {
      if (isFetching) return;
      isFetching = true;
      requestAnimationFrame(() => {
        requestAnimationFrame(async () => {
          try {
            const cartItems = await fetchCartItems();
            buildPlpInventoryMap();
            dispatchBackorderEvent(cartItems);
            maybeDispatchQuickAddToast(cartItems);
          } finally {
            isFetching = false;
          }
        });
      });
    }, DEBOUNCE_MS);
  });

  observer.observe(target, { childList: true, subtree: true });
  observedTargets.add(target);
}

function hasCartDrawer() {
  return !!(
    document.getElementById('CartDrawer-CartItems') ||
    document.querySelector('cart-drawer-items') ||
    document.querySelector('#CartDrawer .drawer__inner')
  );
}

function attachCartDrawerObservers() {
  const candidates = [
    document.getElementById('CartDrawer-CartItems'),
    document.querySelector('cart-drawer-items'),
    document.querySelector('#CartDrawer .drawer__inner'),
  ].filter(Boolean);
  candidates.forEach(attachObserverToTarget);
  return candidates.length > 0;
}

function observeUntilDrawerAvailable() {
  if (attachCartDrawerObservers()) return;
  const bodyObserver = new MutationObserver(() => {
    if (attachCartDrawerObservers()) bodyObserver.disconnect();
  });
  bodyObserver.observe(document.body, { childList: true, subtree: false });
}

// ─── Init ──────────────────────────────────────────────────────────────────

/**
 * Befüllt previousCartQuantities mit dem aktuellen Cart-State beim Seitenload.
 * Verhindert False-Positive-Toasts beim ersten PubSub-Event nach dem Load:
 * ohne Init wäre prevQty für alle Items 0 → jeder Backorder-Artikel im Cart
 * würde beim nächsten unabhängigen Cart-Update einen Toast auslösen.
 */
async function initPreviousCartQuantities() {
  const items = await fetchCartItems();
  previousCartQuantities = new Map(items.map(i => [i.variant_id, i.quantity ?? 0]));
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    buildPlpInventoryMap();
    observeUntilDrawerAvailable();
    initPreviousCartQuantities();
  });
} else {
  buildPlpInventoryMap();
  observeUntilDrawerAvailable();
  initPreviousCartQuantities();
}

document.addEventListener('shopify:section:load', () => {
  buildPlpInventoryMap();
  observeUntilDrawerAvailable();
});

// ─── PubSub-Pfad: Dawn cartUpdate ──────────────────────────────────────────
//
// Primärer Trigger auf Collection/Search-Pages (kein Cart-Drawer nötig).
// quick-add-bulk.js, quick-order-list.js und product-form.js publishen
// PUB_SUB_EVENTS.cartUpdate mit cartData nach jedem erfolgreichen Cart-Call.
//
// Graceful Degradation: fehlen subscribe/PUB_SUB_EVENTS, bleibt nur
// der MutationObserver-Pfad aktiv.

(function attachPubSubCartUpdateListener() {
  if (
    typeof subscribe      !== 'function' ||
    typeof PUB_SUB_EVENTS === 'undefined' ||
    !PUB_SUB_EVENTS.cartUpdate
  ) return;

  subscribe(PUB_SUB_EVENTS.cartUpdate, async (event) => {
    const cartData = event?.cartData;
    let cartItems;

    if (Array.isArray(cartData?.items)) {
      cartItems = cartData.items;
    } else {
      cartItems = await fetchCartItems();
    }

    // renderSections() läuft synchron vor publish() → DOM bereits aktuell → Map neu bauen
    buildPlpInventoryMap();
    maybeDispatchQuickAddToast(cartItems);

    if (!hasCartDrawer()) {
      dispatchBackorderEvent(cartItems);
    }
  });
})();
